// useMessageJump — shared "tap evidence → jump and flash a chat bubble"
// behavior for F1 Mirror evidence rows AND F3 drilldown contributing rows.
//
// Pre-extract: app.jsx owned an inline jumpToMessage closure passed to
// both surfaces, and it carried two latent bugs:
//   1. Scroll headroom too low — a top message scrolled to position 0
//      with the chat-scroll container's 14px top padding, but the new
//      .msg-flash class adds a 6px box-shadow ring that's clipped by
//      the container's overflow:auto and reads visually as "the message
//      is being eaten by the chat header." The new SCROLL_HEADROOM_PX
//      pairs with a bumped container padding-top so the ring fits.
//   2. The 320ms post-setSheet timeout assumed a full-screen Mirror
//      animation; F3 drilldown is a half-sheet with no close anim, so
//      320ms there is "fine, slightly delayed" rather than load-bearing.
//      Same constant works for both since the cost is just a small
//      latency on drilldown jumps.
//
// Both are encapsulated as named constants here — when a surface
// (sheet animation timing, flash duration, scroll padding) changes,
// the next contributor edits one place.

import { useRef, useEffect, useState, useCallback } from 'react';

// Match @keyframes msg-flash duration in styles.css. Diverging values
// would either clip the animation (set lower than keyframes) or leave
// the className applied past the visible animation (set higher).
const FLASH_DURATION_MS = 2400;

// Wait for any closing sheet's animation to settle before scrolling.
// Mirror is a fullsheet (slides off ~280ms); regular sheets close
// instantly via unmount. 320ms covers the slowest path with margin.
const SHEET_CLOSE_DELAY_MS = 320;

// scrollIntoView block alignment. 'center' lands the jumped-to
// message in the middle of the scroll port, so the .msg-flash ring
// can't be visually clipped by the container's top edge regardless
// of where the message sits in the list. Previous 'top' alignment
// caused the ring on a top-of-list message to bleed past the scroll
// container's overflow:auto and read as "scrolled behind the chat
// header" — even though the header is a flex sibling and not
// actually covering the scroll viewport, the visual cue was wrong.
const SCROLL_BLOCK = 'center';

/**
 * @param {{ onBeforeJump?: () => void }} opts
 *   onBeforeJump fires synchronously before the scroll-then-flash
 *   timeline starts. Callers use it to close any open sheet so the
 *   chat is visible when the flash plays.
 */
export function useMessageJump({ onBeforeJump } = {}) {
  const msgRefs = useRef({});
  const [flashMsgId, setFlashMsgId] = useState(null);
  const [expandedMsgId, setExpandedMsgId] = useState(null);
  const timeoutsRef = useRef([]);

  const jumpToMessage = useCallback(
    (id) => {
      onBeforeJump?.();
      const t1 = setTimeout(() => {
        // msgRefs is keyed by msg.id; both numeric fixture IDs and
        // string contract IDs resolve to the same key because object
        // property keys coerce to strings. No normalization is needed
        // for the lookup, but the strict renderer comparison does, so
        // normalize the stored flashMsgId to a string here. See app.jsx,
        // where the className test compares against String(view.id).
        const key = String(id);
        const el = msgRefs.current[key];
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: SCROLL_BLOCK });
        setFlashMsgId(key);
        setExpandedMsgId(key);
        const t2 = setTimeout(() => setFlashMsgId(null), FLASH_DURATION_MS);
        timeoutsRef.current.push(t2);
      }, SHEET_CLOSE_DELAY_MS);
      timeoutsRef.current.push(t1);
    },
    [onBeforeJump]
  );

  // Cancel any pending jump-flash timers on unmount so setState doesn't
  // fire on an unmounted component if a sheet closes mid-animation.
  useEffect(
    () => () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    },
    []
  );

  return {
    msgRefs,
    jumpToMessage,
    flashMsgId,
    expandedMsgId,
    setExpandedMsgId,
  };
}
